import { defineComponent as _defineComponent } from 'vue'
import {computed, onMounted, Ref, ref, watch} from "vue";
import { round } from '@/utils/Helpers';
import { RecipeBreakdown } from '@/models/Recipes/RecipeBreakdown';
import { RecipeIngredientBreakdown } from '@/models/Ingredient/RecipeIngredientBreakdown';
import CalculationsRepository from '@/utils/API/CalculationsRepository';
import { Recipe } from '@/models/Recipes/Recipe';
import {LossType} from "@/models/Recipes/LossType";
import VToggleSwitch from "@/components/vToggleSwitch.vue";
import VRecipeBreakdownTable from "@/components/vRecipeBreakdownTable.vue";
import VPreLoader from "@/components/vPreLoader.vue";
import VModal from "@/components/vModal.vue";
import {useTranslations} from "@/hooks/useTranslations";
import {useNotify} from "@/hooks/useNotify";
import RecipesRepository from "@/utils/API/RecipesRepository";

type vBreakdownReportModalProps = {
  showBreakdownReportModal: boolean;
  parentRecipe: Recipe;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'vBreakdownReportModal',
  props: {
    showBreakdownReportModal: { type: Boolean, default: false },
    parentRecipe: null
  },
  emits: ["on-cancel"],
  setup(__props: any, { emit }: { emit: ({(e: 'on-cancel'): void}), expose: any, slots: any, attrs: any }) {

const props = __props as { showBreakdownReportModal: boolean, parentRecipe: Recipe };



const notifyService = useNotify();
let translations = ref(useTranslations());

function translate(key: string) {
  return translations.value.get(key);
}



// Constants
const countryOptions = computed(() => {
  return [
    {value: '0', name: translate('canada')},
    {value: '1', name: translate('country_us')},
    {value: '2', name: translate('country_uk')},
  ];
});
// Reactive values
const breakdownReportModal: Ref<{
  isOpen: boolean;
  recipe: RecipeBreakdown;
  recipeIngredients: RecipeIngredientBreakdown[];
}> = ref({
  isOpen: false,
  recipe: null,
  recipeIngredients: [],
});

const isLoading = ref(false);
const useRoundedValues = ref(false);
const defaultBreakdownLevel = 2;
const maxBreakdownLevel = 8;
const selectedCountry: Ref<{name: string, value: string}> = ref(countryOptions.value[0]);
const ingredientAllergensByName = ref<Record<string, string>>({});
const recipeLevelAllergensText = ref("--");
const subrecipeLevels = ref<number | null>(null);
const subrecipeLevelsErrorMessage = ref("");

const breakdownLevelOptions = computed(() => {
  const availableMaxLevel = subrecipeLevels.value == null
    ? maxBreakdownLevel
    : Math.min(Math.max(subrecipeLevels.value, defaultBreakdownLevel), maxBreakdownLevel);

  return Array.from(
    { length: maxBreakdownLevel - defaultBreakdownLevel + 1 },
    (_, index) => defaultBreakdownLevel + index
  ).map((level) => ({
    value: level,
    name: String(level),
    disabled: level > availableMaxLevel,
  }));
});

const selectedBreakdownLevel: Ref<{name: string, value: number; disabled?: boolean}> = ref(breakdownLevelOptions.value[0]);

watch(
  () => props.showBreakdownReportModal,
  (newValue: boolean) => {
    if (newValue) {
      translations.value = useTranslations();
      selectedCountry.value = countryOptions.value[0];
      selectedBreakdownLevel.value = breakdownLevelOptions.value[0];
      getRecipeCalculationBreakdown();
      getSubrecipeLevels();
    }
  }
);

onMounted(() => {
  selectedCountry.value = countryOptions.value[0];
  selectedBreakdownLevel.value = breakdownLevelOptions.value[0];
});

function closeModal() {
  emit("on-cancel");
}

function onCountryChanged() {
  getRecipeCalculationBreakdown();
}

function onBreakdownLevelChanged(option: {name: string; value: number; disabled?: boolean}) {
  if (option?.disabled) {
    return;
  }

  getRecipeCalculationBreakdown();
}

function handleBreakdownLevelOptionPointerDown(
  option: {name: string; value: number; disabled?: boolean},
  event: MouseEvent
) {
  if (!option.disabled) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
}

watch(
  breakdownLevelOptions,
  (options) => {
    if (!options.length) {
      return;
    }

    const selectedLevel = selectedBreakdownLevel.value?.value;
    const hasSelectedLevel = options.some((option) => option.value === selectedLevel);

    if (!hasSelectedLevel) {
      selectedBreakdownLevel.value = options[0];
    }
  },
  { immediate: true }
);

const subrecipeLevelDisplayText = computed(() => {
  if (subrecipeLevels.value != null && subrecipeLevels.value >= maxBreakdownLevel) {
    return `This recipe contains ${subrecipeLevels.value} subrecipe levels. Breakdown display supports up to ${maxBreakdownLevel} levels.`;
  }

  if (subrecipeLevels.value != null) {
    return `Subrecipe levels: ${subrecipeLevels.value}`;
  }

  if (subrecipeLevelsErrorMessage.value) {
    return `Subrecipe levels: ${subrecipeLevelsErrorMessage.value}`;
  }

  return "Subrecipe levels: --";
});

const subrecipeLevelMessageClass = computed(() => ({
  "subrecipe-level-message": true,
  "alert alert-warning border border-warning": subrecipeLevels.value != null && subrecipeLevels.value >= maxBreakdownLevel,
}));

const tableHeaders = computed(() => {
  const staticHeaders = [
    'Ingredient',
    'Allergens',
    'Quantity',
    'Quantity (G)',
    'Weight %'
  ];

  const firstIngredient = breakdownReportModal.value.recipe?.nutrientFacts?.ingredients?.[0];
  if (!firstIngredient) {
    return staticHeaders;
  }

  const dynamicHeaders = Object.keys(firstIngredient)
    .filter((key) =>
      key !== 'ingredientName' &&
      key !== 'allergenText' &&
      key !== 'weightPercent' &&
      key !== 'quantity' &&
      key !== 'quantityInGrams' &&
      key !== 'quantityInGramsAfterLoss' &&
      key !== 'childRecipeBreakdown' &&
      key !== 'foodClassification'
    );

  // HACK: Added sugar has a naming convention mismatch
  const addedSugarIndex = dynamicHeaders.indexOf('addedSugar');
  if (addedSugarIndex > 0) {
    dynamicHeaders[addedSugarIndex] = 'addedSugar';
  }

  return [...staticHeaders, ...dynamicHeaders];
});

const slicedTableHeaders = computed(() => tableHeaders.value.slice(5));

function normalizeIngredientLookupKey(value: string) {
  return value.trim().toLowerCase();
}

function hydrateIngredientAllergenMapFromBreakdown(recipe: RecipeBreakdown | null) {
  const allergensByName = new Map<string, string>();

  const addIngredients = (ingredients: RecipeIngredientBreakdown[] = []) => {
    ingredients.forEach((ingredient) => {
      if (ingredient?.ingredientName && ingredient?.allergenText) {
        allergensByName.set(normalizeIngredientLookupKey(ingredient.ingredientName), ingredient.allergenText);
      }

      if (ingredient?.childRecipeBreakdown?.nutrientFacts?.ingredients?.length) {
        addIngredients(ingredient.childRecipeBreakdown.nutrientFacts.ingredients);
      }
    });
  };

  addIngredients(recipe?.nutrientFacts?.ingredients || []);

  ingredientAllergensByName.value = Array.from(allergensByName.entries()).reduce<Record<string, string>>((acc, [name, allergenText]) => {
    acc[name] = allergenText;
    return acc;
  }, {});
}

function toggleRoundedDisplay() {
  useRoundedValues.value = !useRoundedValues.value;
}

function getUserVisibleErrorMessage(error: any) {
  return error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
    || error?.response?.data?.message
    || error?.response?.data?.error
    || error?.message
    || translate("network-error");
}

function getSubrecipeLevels() {
  subrecipeLevels.value = null;
  subrecipeLevelsErrorMessage.value = "";

  return RecipesRepository.getSubrecipeLevels(props.parentRecipe.id)
    .then((response) => {
      subrecipeLevels.value = response.data.subrecipeLevels;
    })
    .catch((error) => {
      const message = getUserVisibleErrorMessage(error);
      subrecipeLevels.value = null;
      subrecipeLevelsErrorMessage.value = message;
      notifyService.error(message);
    });
}

function getRecipeCalculationBreakdown(){
  let model = {
    recipeId: props.parentRecipe.id,
    country: selectedCountry.value.value,
    breakdownLevel: selectedBreakdownLevel.value.value,
    useRoundedValues: useRoundedValues.value,
  };
  isLoading.value = true;

  CalculationsRepository.getRecipeCalculationBreakdown(model)
    .then((response) => {
      breakdownReportModal.value.recipe = response.data.result;
      breakdownReportModal.value.recipeIngredients = response.data.result.nutrientFacts.ingredients;
      recipeLevelAllergensText.value = response.data.result.recipeAllergenText || "--";
      hydrateIngredientAllergenMapFromBreakdown(response.data.result);
      isLoading.value = false;
    })
    .catch((error) => {
      notifyService.error(getUserVisibleErrorMessage(error));
      isLoading.value = false;
    });
}

// Download breakdown recipe
const exportRecipeBreakdownToExcelURL = computed(() => {
  let url = `${process.env.VUE_APP_BASE_DOMAIN}/api/processes/recipe/reports/ExportBreakdownPerRecipe?`;

  if(breakdownReportModal.value.recipe){
    const queryParams = `country=${selectedCountry.value.value}&recipeId=${props.parentRecipe.id}&breakdownLevel=${selectedBreakdownLevel.value.value}&useRoundedValues=${useRoundedValues.value}`;
    url += queryParams;
  }

  return url;
});

return { __sfc: true,props, notifyService, translations, translate, emit, countryOptions, breakdownReportModal, isLoading, useRoundedValues, defaultBreakdownLevel, maxBreakdownLevel, selectedCountry, ingredientAllergensByName, recipeLevelAllergensText, subrecipeLevels, subrecipeLevelsErrorMessage, breakdownLevelOptions, selectedBreakdownLevel, closeModal, onCountryChanged, onBreakdownLevelChanged, handleBreakdownLevelOptionPointerDown, subrecipeLevelDisplayText, subrecipeLevelMessageClass, tableHeaders, slicedTableHeaders, normalizeIngredientLookupKey, hydrateIngredientAllergenMapFromBreakdown, toggleRoundedDisplay, getUserVisibleErrorMessage, getSubrecipeLevels, getRecipeCalculationBreakdown, exportRecipeBreakdownToExcelURL, round, LossType, VToggleSwitch, VRecipeBreakdownTable, VPreLoader, VModal }
}

})